import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { User, Bot, Copy, Check, Clock } from 'lucide-react';
import { cn, formatTime, copyToClipboard } from '@/utils/helpers';
import type { ChatMessage } from '@/types';

interface MessageProps {
  message: ChatMessage;
}

export function Message({ message }: MessageProps): JSX.Element {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === 'user';
  const isLoading = message.type === 'loading';

  const handleCopy = async () => {
    const success = await copyToClipboard(message.content);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      className={cn(
        'group flex gap-4 p-4',
        isUser ? 'bg-primary-50/50' : 'bg-white'
      )}
    >
      <div
        className={cn(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
          isUser ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600'
        )}
      >
        {isUser ? <User size={18} /> : <Bot size={18} />}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-sm">
            {isUser ? '你' : '助手'}
          </span>
          <span className="text-gray-400 text-xs flex items-center gap-1">
            <Clock size={12} />
            {formatTime(message.timestamp)}
          </span>
        </div>

        <div className="relative">
          {isLoading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <span className="text-sm">思考中...</span>
            </div>
          ) : (
            <>
              <div className="markdown text-sm leading-relaxed text-gray-800">
                <ReactMarkdown>{typeof message.content === 'string' ? message.content : String(message.content || '')}</ReactMarkdown>
              </div>

              {!isUser && (
                <button
                  onClick={handleCopy}
                  className="absolute -right-2 -top-2 p-1.5 rounded-lg bg-white shadow-sm border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-50"
                  title="复制内容"
                >
                  {copied ? (
                    <Check size={14} className="text-green-500" />
                  ) : (
                    <Copy size={14} className="text-gray-400" />
                  )}
                </button>
              )}
            </>
          )}
        </div>

        {/* VideoCard, CommentsList, AnalysisCard removed - content already rendered above */}
      </div>
    </div>
  );
}


